import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { InputHandler } from '../../../../../viewer/input-handler';
import { truncateText } from '../../../../utils/styled-helpers';
import { Icon } from '../../../icon/icon';
import { Stack } from '../../../stack/stack';
import { RightMenuWindowContext } from '../hooks/use-right-menu-window';

type Props = {
  icon: IconProp;
  name: string;
  className?: string;
  children?: ReactNode;
};

export const RightPanelHeader = (props: Props) => {
  const { t } = useTranslation();
  const menuWindow = React.useContext(RightMenuWindowContext);

  return (
    <Component alignItems="center" className={props.className} direction="row" spacing={0.8}>
      <Icon icon={props.icon} />
      <NameLabel title={props.name}>{props.name}</NameLabel>
      {props.children}
      {/* In the right menu, which can be opened in a window of its own. */}
      {menuWindow &&
        (menuWindow.inWindow ? (
          <Icon
            fixedWidth={true}
            icon={['fal', 'square-arrow-down-left']}
            mouseDownStyle={{ opacity: 0.5 }}
            title={t('dialog.popIn', { ns: 'common' })}
            onClick={menuWindow.putBack}
            onHoverStyle={{ icon: ['fas', 'square-arrow-down-left'] }}
          />
        ) : (
          <Icon
            fixedWidth={true}
            icon={['fal', 'arrow-up-right-from-square']}
            mouseDownStyle={{ opacity: 0.5 }}
            title={t('dialog.popOut', { ns: 'common' })}
            onClick={menuWindow.popOut}
            onHoverStyle={{ icon: ['fas', 'arrow-up-right-from-square'] }}
          />
        ))}
      {/* In a window of its own it is closed with the close button of the window. */}
      {!menuWindow?.inWindow && (
        <Icon
          fixedWidth={true}
          icon={['fal', 'close']}
          mouseDownStyle={{ opacity: 0.5 }}
          title={t('close', { ns: 'common' })}
          onClick={() => InputHandler.instance.setTool('select')}
          onHoverStyle={{ icon: ['fas', 'close'] }}
        />
      )}
    </Component>
  );
};

const Component = styled(Stack)`
  background-color: #dadada;
  padding: 0.8em;
  border-bottom: 1px solid #aaa;
  z-index: 1;

  * {
    font-size: 14px;
  }
`;

const NameLabel = styled.label`
  flex-grow: 1;

  // Increase line height to avoid cutting off the bottom of the characters and counter.
  line-height: 1.2em;
  // Negative margin to counter the extra height line height gives to the element.
  margin: -0.1em 0;

  ${truncateText(1)}
`;
